import { useEffect, useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";
import { WALLPAPERS } from "../../config/wallpapers";
import { useDesktop } from "../../pages/HomePage/utils/DesktopContext";
import { useSystemWindows } from "../../pages/HomePage/utils/useSystemWindows";

// Right-click on empty desktop: Refresh / Wallpaper / Properties, like 98.
const DesktopContextMenu = () => {
  const [position, setPosition] = useState<{ x: number; y: number } | null>(
    null
  );
  const [showWallpapers, setShowWallpapers] = useState(false);
  const { wallpaper, setWallpaper, refreshDesktop } = useDesktop();
  const { openDisplayProperties, openSystemProperties } = useSystemWindows();

  useEffect(() => {
    const handleContextMenu = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.closest(
          "[data-window], [data-taskbar], [data-desktop-icon], [data-context-menu]"
        ) ||
        !target.closest("[data-desktop-surface]")
      ) {
        return;
      }
      // keep the menu on screen
      setPosition({
        x: Math.min(e.clientX, window.innerWidth - 190),
        y: Math.min(e.clientY, window.innerHeight - 150),
      });
      setShowWallpapers(false);
    };
    const close = (e: Event) => {
      if ((e.target as HTMLElement).closest?.("[data-context-menu]")) return;
      setPosition(null);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPosition(null);
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", handleKey);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("resize", close);
    };
  }, []);

  const run = (action: () => void) => () => {
    setPosition(null);
    action();
  };

  return (
    <AnimatePresence>
      {position && (
        <Menu
          data-context-menu
          style={{ left: position.x, top: position.y }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.08 } }}
          transition={{ duration: 0.12 }}
        >
          <Item $disabled>Arrange Icons</Item>
          <Item
            onClick={run(refreshDesktop)}
            onMouseEnter={() => setShowWallpapers(false)}
          >
            Refresh
          </Item>
          <Separator />
          <Item
            onMouseEnter={() => setShowWallpapers(true)}
            $highlight={showWallpapers}
          >
            Wallpaper
            <Arrow>▸</Arrow>
            {showWallpapers && (
              <SubMenu $flip={position.x > window.innerWidth - 360}>
                {WALLPAPERS.map((option) => (
                  <Item
                    key={option.id}
                    onClick={run(() => setWallpaper(option.id))}
                  >
                    <Check>{wallpaper === option.id ? "•" : ""}</Check>
                    {option.name}
                  </Item>
                ))}
              </SubMenu>
            )}
          </Item>
          <Separator />
          <Item
            onClick={run(openSystemProperties)}
            onMouseEnter={() => setShowWallpapers(false)}
          >
            About This Computer
          </Item>
          <Item
            onClick={run(openDisplayProperties)}
            onMouseEnter={() => setShowWallpapers(false)}
          >
            Properties
          </Item>
        </Menu>
      )}
    </AnimatePresence>
  );
};

export default DesktopContextMenu;

const Menu = styled(motion.div)`
  position: fixed;
  z-index: 99600;
  min-width: 170px;
  padding: 2px;
  background: ${(p) => p.theme.MSBackground};
  box-shadow:
    ${(p) => p.theme.standardBoxShadow},
    2px 2px 0 rgba(0, 0, 0, 0.25);
  font-size: ${(p) => p.theme.fontNormal}px;
  transform-origin: top left;
`;

const Item = styled.div<{ $disabled?: boolean; $highlight?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 4px 20px 4px 8px;
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  white-space: nowrap;
  color: ${(p) => (p.$disabled ? "#808080" : p.$highlight ? "#fff" : "#000")};
  text-shadow: ${(p) => (p.$disabled ? "1px 1px 0 #fff" : "none")};
  background: ${(p) => (p.$highlight ? "#00007b" : "transparent")};

  &:hover {
    ${(p) => !p.$disabled && "background: #00007b; color: #fff;"}
  }
`;

const Check = styled.span`
  width: 12px;
  font-weight: 700;
`;

const Arrow = styled.span`
  margin-left: auto;
  padding-left: 12px;
  font-size: 10px;
`;

const Separator = styled.div`
  height: 2px;
  margin: 3px 2px;
  border-top: 1px solid #808080;
  border-bottom: 1px solid #fff;
`;

const SubMenu = styled.div<{ $flip: boolean }>`
  position: absolute;
  ${(p) => (p.$flip ? "right: calc(100% - 2px);" : "left: calc(100% - 2px);")}
  top: -2px;
  min-width: 140px;
  padding: 2px;
  color: #000;
  background: ${(p) => p.theme.MSBackground};
  box-shadow: ${(p) => p.theme.standardBoxShadow};
`;
